import React, { useMemo } from 'react';

import NiceDropdown from 'components/NiceDropdown';
import { StyleFilterLabel } from 'components/Filters/styles';
import { useAllAccountScreenersQuery } from 'api/useAllAccountScreenersQuery';

// Accounts can hold thousands of screeners; rendering them all locks up the
// dropdown, so the rest is reached by typing. Screeners already picked are shown
// on top of this, so the cap can never hide the current selection.
const MAX_VISIBLE_UNCHECKED_SCREENERS = 100;

// Digits only — no decimals, signs or exponents. Matches what the api's
// FILTER_VALIDATE_INT accepts for the same value, so both ends agree on which ids
// count rather than each coercing the leftovers its own way.
const SCREENER_ID_PATTERN = /^\d+$/;

// A screener label runs to ~35 characters, so spelling out every pick overflows the
// filter chip and pushes the row onto a second line. Past this many, the chip
// summarises instead.
const MAX_CHIP_TITLES = 2;

/**
 * Screener titles repeat across studies, so the study has to be part of the label
 * for an option to be identifiable — and searchable by either name.
 *
 * @param {{screener_title: string, study_title: string}} screener
 * @returns {string}
 */
function buildScreenerLabel(screener) {
    const screenerTitle = screener.screener_title ? screener.screener_title : 'Untitled screener';

    return `${screenerTitle} - ${screener.study_title}`;
}

/**
 * Value editor for the panel's "Completed Screener" filter — the same chip plus
 * searchable checkbox dropdown as "Added To Panel" and the study filters.
 *
 * It exists as its own component only so the screener list can be fetched here
 * instead of by `ParticipantsPage`: the response runs to hundreds of KB on large
 * accounts, and mounting this is exactly the moment it is needed. React Query
 * dedupes, so several filter rows share one request.
 *
 * Checking several screeners in one row ORs them; a second filter row ANDs, which
 * is how "completed A and B" is expressed. Both the option list and the chip are
 * capped, because either one grows without bound otherwise.
 *
 * @param {Object} props
 * @param {(number|string)[]} [props.values] Selected screener ids.
 * @param {boolean} props.isOpen Whether the dropdown is showing.
 * @param {() => void} props.onToggle Toggle the dropdown from the chip.
 * @param {() => void} props.onClose Close it without committing (Cancel / outside click).
 * @param {(screenerIds: (number|string)[]) => void} props.onChange Commit, on Done.
 * @param {number} props.positionLeft Horizontal offset of the dropdown, measured by
 *   the parent chip so the panel lines up with the filter's left edge.
 */
export function CompletedScreenerFilterValue({ values = [], isOpen, onToggle, onClose, onChange, positionLeft }) {
    // Integration-backed screeners record completions too, so the filter has to
    // offer them; screeners of a deleted study are not worth filtering a panel by.
    // Both differ from the endpoint's defaults — see getAllAccountScreeners.
    const { data: screeners, isLoading, isError } = useAllAccountScreenersQuery({
        includeIntegrations: true,
        excludeDeletedStudies: true
    });

    const items = useMemo(() => {
        // `label: true` renders as the dropdown's non-selectable section heading, so a
        // slow or failed load can't be mistaken for "this account has no screeners".
        if (isLoading) {
            return [{ id: 'loading', title: 'Loading screeners…', label: true }];
        }

        if (isError) {
            return [{ id: 'error', title: "Couldn't load screeners", label: true }];
        }

        if (!screeners) {
            return [];
        }

        return screeners.map(screener => ({
            id: screener.screener_id,
            title: buildScreenerLabel(screener)
        }));
    }, [screeners, isLoading, isError]);

    // The filters live in the query string, so an id can arrive as the string "8" from
    // a hand-edited or shared URL. The dropdown matches its options with `includes`,
    // which is strict — "8" would leave the box unticked while the chip named it, and
    // Done would then drop it. Normalising once here is what keeps the two in step.
    //
    // An id is a positive integer and nothing else. Truncating "8.9" to 8 would tick a
    // screener the value never named; the api applies the same rule to the same value.
    const selectedScreenerIds = useMemo(
        () =>
            values
                // Coercion alone would read `true` as 1 and `[8]` as 8; the api takes
                // neither, so neither does the box the user reads it from.
                .filter(value => typeof value === 'number' || typeof value === 'string')
                .filter(value => SCREENER_ID_PATTERN.test(String(value).trim()))
                .map(value => parseInt(value, 10))
                .filter(screenerId => screenerId > 0),
        [values]
    );

    const chipText = useMemo(() => {
        if (!screeners) {
            // The names live in a list that has not arrived, or failed to. Saying how
            // many are selected beats an empty chip, which reads as a filter with no
            // value even though this one is filtering.
            return values.length === 1 ? '1 screener' : `${values.length} screeners`;
        }

        const titles = values.map(value => {
            const screener = screeners.find(obj => obj.screener_id == value);

            // A screener deleted since the filter was saved still filters, so name it
            // by id rather than leaving a gap in the middle of the sentence.
            return screener ? buildScreenerLabel(screener) : `screener #${value}`;
        });

        if (titles.length <= MAX_CHIP_TITLES) {
            return titles.join(' or ');
        }

        const shown = titles.slice(0, MAX_CHIP_TITLES).join(' or ');

        return `${shown} or ${titles.length - MAX_CHIP_TITLES} more`;
    }, [screeners, values]);

    return (
        <>
            <StyleFilterLabel className="filterLabel text-secondary" onClick={onToggle}>
                <span style={{ padding: '0 2px' }}>is</span>
                {values.length ? chipText : <em>missing value</em>}
            </StyleFilterLabel>

            {isOpen && (
                <NiceDropdown
                    renderDoneButton
                    adjustHeight
                    width="340px"
                    onClose={onClose}
                    showSearch
                    listType="checkbox"
                    items={items}
                    maxVisibleUncheckedItems={MAX_VISIBLE_UNCHECKED_SCREENERS}
                    // A copy: with renderDoneButton the dropdown accumulates the
                    // selection by mutating this array in place, so it must not be
                    // handed the page's filter state, nor the memoised array above.
                    value={[...selectedScreenerIds]}
                    onChange={onChange}
                    positionLeft={positionLeft}
                />
            )}
        </>
    );
}

export default CompletedScreenerFilterValue;
